import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '../hooks/useApi';

const EmailLogTab = ({ riskId }) => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [triggering, setTriggering] = useState(false);
    const [triggerResult, setTriggerResult] = useState(null);
    const apiCall = useApi();

    const fetchLogs = useCallback(() => {
        if (!riskId) return;
        setLoading(true);
        apiCall('GET', `/allRemediations/email-logs/by-risk?riskId=${encodeURIComponent(riskId)}`)
            .then(data => setLogs(data || []))
            .catch(() => {})
            .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [riskId]);

    useEffect(() => { fetchLogs(); }, [fetchLogs]);

    const handleTrigger = async () => {
        setTriggering(true);
        setTriggerResult(null);
        try {
            const result = await apiCall('POST', '/allRemediations/trigger-reminders');
            setTriggerResult(result);
            if (result?.emailsSent > 0) fetchLogs();
        } catch (err) {
            setTriggerResult({ summary: 'Failed to trigger reminder check. Check permissions.' });
        } finally {
            setTriggering(false);
        }
    };

    const sortedLogs = [...logs].sort((a, b) => new Date(b.sentDate) - new Date(a.sentDate));

    const formatTime = (dateStr) => {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    };

    const formatDateLabel = (dateStr) => {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
    };

    const getReminderLabel = (days) => {
        if (days > 0) return `${days} days before due`;
        if (days < 0) return `${Math.abs(days)} days overdue`;
        return 'On due date';
    };

    const getReminderConfig = (days) => {
        if (days === 15) return { dot: 'bg-red-500',    icon: '🔴', urgency: 'text-red-600' };
        if (days === 30) return { dot: 'bg-orange-500', icon: '🟠', urgency: 'text-orange-600' };
        if (days === 45) return { dot: 'bg-blue-500',   icon: '🔵', urgency: 'text-blue-600' };
        if (days < 0)   return { dot: 'bg-red-700',    icon: '⚠️', urgency: 'text-red-700' };
        return              { dot: 'bg-gray-400',    icon: '📧', urgency: 'text-gray-600' };
    };

    const bold = (val, colorClass = 'text-gray-800') => (
        <span className={`font-semibold ${colorClass}`}>{val}</span>
    );

    const TabHeader = () => (
        <div className="mb-4 pb-3 border-b border-gray-200">
            <div className="flex items-center gap-2 mb-1">
                <span className="text-base">📬</span>
                <h3 className="text-sm font-semibold text-gray-800">Overdue Risk Register — Email Log</h3>
            </div>
            <p className="text-xs text-gray-500 ml-6">
                Automated reminder emails sent to assignees and approvers when a remediation target date is approaching or overdue. Emails are triggered at 45, 30, and 15 days before the due date, then every 7 days once overdue.
            </p>
        </div>
    );

    if (loading) {
        return (
            <div>
                <TabHeader />
                <div className="flex items-center justify-center py-12">
                    <p className="text-sm text-gray-500">Loading email log...</p>
                </div>
            </div>
        );
    }

    const TriggerButton = ({ center = false }) => (
        <div className={`flex flex-col gap-2 ${center ? 'items-center mt-4' : 'items-end'}`}>
            <button
                onClick={handleTrigger}
                disabled={triggering}
                className="px-3 py-1.5 text-xs bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {triggering ? 'Checking...' : 'Run Reminder Check Now'}
            </button>
            {triggerResult && <TriggerResultPanel result={triggerResult} center={center} />}
        </div>
    );

    const TriggerResultPanel = ({ result, center }) => {
        const hasSent = result.emailsSent > 0;
        const hasUpcoming = result.upcomingTriggers?.length > 0;
        return (
            <div className={`text-xs rounded-md border p-2 mt-1 ${center ? 'max-w-xs text-center' : 'w-full'} ${hasSent ? 'bg-green-50 border-green-200 text-green-800' : 'bg-gray-50 border-gray-200 text-gray-600'}`}>
                <p className="font-medium">{result.summary}</p>
                {(result.totalRemediations > 0 || result.skippedNoDate > 0) && !hasSent && (
                    <p className="mt-1 text-gray-400">
                        {result.totalRemediations} total · {result.skippedNoDate} no date · {result.skippedNoTrigger} not trigger day
                        {result.skippedNoAssignee > 0 ? ` · ${result.skippedNoAssignee} no assignee` : ''}
                    </p>
                )}
                {hasUpcoming && (
                    <div className="mt-2 text-left">
                        <p className="font-medium text-gray-500 mb-1">Upcoming triggers:</p>
                        {result.upcomingTriggers.map((t, i) => (
                            <div key={i} className="mb-1 pl-1 border-l-2 border-indigo-300">
                                <p className="font-medium text-gray-700 truncate">{t.riskTitle}</p>
                                <p className="text-gray-400">
                                    {t.daysUntilDue > 0 ? `Due in ${t.daysUntilDue}d` : `${Math.abs(t.daysUntilDue)}d overdue`}
                                    {' · '}Target: {t.targetDate}
                                    {' · '}Next email: <span className="text-indigo-600 font-medium">{t.nextTriggerDate}</span>
                                </p>
                                <p className="text-gray-400">{t.assigneeInfo}</p>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    if (sortedLogs.length === 0) {
        return (
            <div>
                <TabHeader />
                <div className="flex flex-col items-center justify-center py-8 text-center">
                    <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mb-3 text-xl">📧</div>
                    <p className="text-sm font-medium text-gray-500">No reminder emails sent yet</p>
                    <p className="text-xs text-gray-400 mt-1 mb-3">Emails fire at 45, 30, 15 days before due date or every 7 days when overdue</p>
                    <TriggerButton center />
                </div>
            </div>
        );
    }

    return (
        <div className="relative">
            <TabHeader />
            {/* Trigger button at top */}
            <div className="mb-3">
                <TriggerButton />
            </div>
            {/* Vertical line */}
            <div className="absolute left-[4.75rem] top-14 bottom-3 w-px bg-gray-200 pointer-events-none" />

            {sortedLogs.map((log, idx) => {
                const cfg = getReminderConfig(log.reminderDays);
                const timeStr = formatTime(log.sentDate);
                const dateLabel = formatDateLabel(log.sentDate);
                const prevDateLabel = idx > 0 ? formatDateLabel(sortedLogs[idx - 1].sentDate) : null;
                const showDateSep = idx === 0 || dateLabel !== prevDateLabel;
                const isApprover = log.recipientRole === 'Approver';

                return (
                    <div key={log.id ?? idx}>
                        {showDateSep && (
                            <div className={`flex items-center ${idx > 0 ? 'mt-4' : ''} mb-2`}>
                                <div className="w-[4.25rem] flex-shrink-0 pr-2 text-right">
                                    <span className="text-xs font-bold text-gray-500 whitespace-nowrap">{dateLabel}</span>
                                </div>
                                <div className="w-8 flex-shrink-0" />
                                <div className="flex-1 h-px bg-gray-200" />
                            </div>
                        )}
                        <div className="flex items-start mb-2">
                            <div className="w-[4.25rem] flex-shrink-0 text-right pr-2 pt-0.5">
                                <span className="text-[11px] text-gray-400 whitespace-nowrap">{timeStr}</span>
                            </div>
                            <div className="w-8 flex-shrink-0 flex justify-center relative z-10 pt-0.5">
                                <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] ${cfg.dot}`}>
                                    {cfg.icon}
                                </div>
                            </div>
                            <div className="flex-1 min-w-0 ml-2">
                                <p className="text-xs text-gray-600 leading-snug">
                                    Reminder sent to {bold(isApprover ? 'Approver' : 'Assignee')}{' '}
                                    {bold(log.recipientEmail, 'text-indigo-600')}
                                    {' — '}{bold(getReminderLabel(log.reminderDays), cfg.urgency)}
                                </p>
                                <p className="text-[11px] text-indigo-600 font-medium mt-0.5">
                                    📅 Target: {formatDate(log.targetDate)}
                                </p>
                                {log.assigneeName && (
                                    <p className="text-[11px] text-gray-400 mt-0.5">
                                        Assignee: {log.assigneeName}
                                        {log.approverName ? ` · Approver: ${log.approverName}` : ''}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default EmailLogTab;
